// Layer: domain. No imports from infrastructure/presentation.
// Mirrors storia.domain.*. Kept honest by `src/contract.test.tsx`, which drives every
// console write against `contracts/console-contract.json` — the API's own OpenAPI
// document and its real responses, generated by the API's test suite. An earlier
// version of this comment cited `npm run check:contract`, a script that did not exist.

export type ActionStatus =
  | "queued"
  | "auto_approved"
  | "executed"
  | "rejected"
  | "reversed";

export type Decision = "approve" | "reject" | "reverse";

export interface Action {
  id: string;
  operator_id: string;
  stay_id: string;
  property_id: string;
  playbook_id: string;
  kind: string;
  payload: Record<string, string | number | boolean | null>;
  reasoning: string[];
  status: ActionStatus;
  reversible: boolean;
  created_at: string;
}

export interface ShiftView {
  arriving: Action[];
  in_stay: Action[];
  departing: Action[];
}

export type Arc = keyof ShiftView;

export const ARCS: readonly Arc[] = ["arriving", "in_stay", "departing"] as const;

export const ARC_LABELS: Record<Arc, string> = {
  arriving: "Arriving",
  in_stay: "In stay",
  departing: "Departing",
};

export type NodeKind = "trigger" | "guardrail" | "action";

export type OnPass = "auto_execute" | "queue";
export type OnFail = "block" | "escalate";

export interface TriggerSpec {
  signal_kind: string;
  when?: string | null;
}

export interface GuardrailSpec {
  predicate: string;
  on_pass: OnPass;
  on_fail: OnFail;
}

export interface ActionSpec {
  kind: string;
  template: string;
  auto_approve: boolean;
  reversible: boolean;
}

export type PlaybookNode =
  | { key: string; kind: "trigger"; spec: TriggerSpec; next: string[] }
  | { key: string; kind: "guardrail"; spec: GuardrailSpec; next: string[] }
  | { key: string; kind: "action"; spec: ActionSpec; next: string[] };

export interface Playbook {
  id: string;
  name: string;
  /** Assigned by the store, returned on a read, and NOT accepted on a write: the API's
   *  `PlaybookIn` forbids it, because a client that picks its own version can overwrite
   *  a concurrent edit. */
  version: number;
  /** Returned on a read, derived by the API from the graph. Also not accepted back. */
  required_facts?: string[];
  nodes: PlaybookNode[];
}

/** Action kinds the engine may emit. Mirrors domain.playbook.ALLOWED_ACTION_KINDS. */
export const ALLOWED_ACTION_KINDS = [
  "pms.note.add",
  "pms.room.assign",
  "messaging.draft",
  "operator.alert",
] as const;

export interface MergeCandidate {
  id: number;
  candidate_a_id: string;
  candidate_b_id: string;
  confidence: number;
  evidence: Record<string, string>;
  created_at: string | null;
}

export interface AuditEntry {
  actor: string;
  action: string;
  correlation_id: string;
  before_hash: string | null;
  after_hash: string;
  prev_digest: string | null;
  digest: string;
  emitted_at: string | null;
}

export interface AuditFeed {
  entries: AuditEntry[];
  /** False means the hash chain failed replay: a tamper signal, not a warning. */
  chain_intact: boolean;
}

export interface PropertyKpis {
  operator_id: string;
  property_id: string;
  window_start: string;
  window_end: string;
  revenue_per_guest_night: number;
  repeat_stay_rate: number;
  avg_review_score: number;
  median_recovery_minutes: number;
  staff_dau_ratio: number;
  storia_action_count: number;
}

export interface GroupKpis {
  properties: PropertyKpis[];
  revenue_per_guest_night: number;
  repeat_stay_rate: number;
  avg_review_score: number;
  median_recovery_minutes: number;
  staff_dau_ratio: number;
  total_action_count: number;
}

/** Why a request failed, as the caller should act on it. */
/** One row of the guest journey: the same moment read from two sides.
 *
 * `guestSide` is empty when the guest perceived nothing — a queued suggestion is real
 * in the system and a non-event for the guest. The view must render that emptiness
 * rather than filling it, which is why this is a string and not an optional. */
export interface JourneyMoment {
  occurred_at: string;
  sequence: number;
  origin: "signal" | "action";
  kind: string;
  guest_side: string;
  system_side: string[];
  status: string | null;
  reversible: boolean | null;
  awaiting_human: boolean;
}

export interface GuestJourney {
  guest_id: string;
  display_name: string;
  moments: JourneyMoment[];
}

export interface GuestSummary {
  id: string;
  display_name: string;
}

export type ApiFailure =
  | { kind: "unauthenticated" }
  | { kind: "forbidden"; detail: string }
  | { kind: "not_found" }
  | { kind: "conflict"; detail: string }
  | { kind: "invalid"; detail: string }
  | { kind: "unavailable"; detail: string };

export class ApiError extends Error {
  constructor(readonly failure: ApiFailure) {
    super(describeFailure(failure));
    this.name = "ApiError";
  }
}

export function describeFailure(failure: ApiFailure): string {
  switch (failure.kind) {
    case "unauthenticated":
      return "Your session has expired. Sign in again.";
    case "forbidden":
      return join("You do not have access to this.", failure.detail);
    case "not_found":
      return "Not found.";
    // A lead sentence of our own, so neither of these can render as an empty alert.
    // `sentence("")` is "", and both a 409 and a 422 can arrive with no readable detail
    // at all: `Error("")` in an alert box is a decision that silently did not happen.
    case "conflict":
      return join("STORIA could not apply that.", failure.detail);
    case "invalid":
      return join("STORIA rejected that request.", failure.detail);
    case "unavailable":
      return join("STORIA is unreachable.", failure.detail);
  }
}

/**
 * Join a lead sentence to a detail, keeping both as sentences.
 *
 * The console used to render `STORIA is unreachable. the request timed out`, because the
 * detail is a fragment written to be read mid-sentence and it was concatenated after a
 * full stop instead. Details also arrive from the API, where nothing guarantees their
 * shape, so the casing and the final stop are applied here rather than trusted.
 */
function join(lead: string, detail: string): string {
  const tail = sentence(detail);
  return tail ? `${lead} ${tail}` : lead;
}

function sentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const capitalised = trimmed[0].toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}
